import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IntlService } from '@progress/kendo-angular-intl';
import { parse } from 'date-fns';
import { BehaviorSubject, firstValueFrom, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { QPointNumberFormatPipe } from '../pipes/q-number-format.pipe';
import { DI_LOCALIZATION_INFO, ILanguage, IQPointLocalizationInfo } from './localization';
import { QPointIntlService } from './qpoint-intl-service';
import { cloneDeep } from 'lodash';
import { utcToZonedTime } from 'date-fns-tz';

@Injectable({
  providedIn: 'root'
})
export class QPointLocalizationService {

  public readonly LocaleRegex = new RegExp('^[a-zA-Z]+');
  private _userLocale$: Subject<string> = new Subject<string>();
  public userLocale$ = this._userLocale$.asObservable();
  private _languageLoaded$ = new ReplaySubject();
  public languageLoaded$ = this._languageLoaded$.asObservable();
  private cachedLanguages$: BehaviorSubject<ILanguage[]> = new BehaviorSubject([]);
  globalTimezone = 'Europe/Vienna';



  constructor(private translate: TranslateService,
              @Inject(DI_LOCALIZATION_INFO) private localizationInfoService: IQPointLocalizationInfo,
              @Inject(LOCALE_ID) private localeId: string,
              private intlService: IntlService) {
    this._userLocale$
      .pipe(distinctUntilChanged())
      .pipe(map(x => {
        if (x) {
          return x;
        } else {
          return this.localizationInfoService.defaultLocale;
        }
      }))
      .pipe(tap(async (x) => {
        this._currentLocale = x;
        this.changeLanguage(x);
        this.cachedLanguages$.next(await this.localizationInfoService.getLanguages(x));
      }))
      .subscribe(value => {
        console.log('userLocale changed', value);
      });
  }

  private _currentLocale: string;

  public get cultureForDateFormatting() {
    return this.localizationInfoService.getCultureInfoDatetime();
  }

  public newDate(): Date {
    return new Date();
  }

  public moment(value?: Date | string, inTimezone?: string): Date {
    const inputDate = value ? new Date(value) : new Date();
    return inTimezone
    ? utcToZonedTime(inputDate, inTimezone) // JavaScript Dates are internally stored in UTC
    : inputDate;
  }

  public momentByFormat(value: string, format: any): Date {

    return parse(value, format, new Date());
  }

  public setGlobalTimezone(timezone: string) {
    this.globalTimezone = timezone;
  }

  public get currentLocale(): string {
    return this._currentLocale || 'de';
  }

  public setUserLocale(userLocale: string) {
    this._userLocale$.next(userLocale);
  }

  public getTranslation(path: string, interpolateParams?: object): string {
    return this.translate.instant(path, interpolateParams);
  }

  public async translateTo(language: string, key: string): Promise<string> {
      // workaround to get a translated value from a language other than the current language
      // see https://stackoverflow.com/questions/50706158/angular-ngx-translate-gettranslation-issue
      console.log("translation");
      const translateDeepCopy = cloneDeep(this.translate);
      const translations = await translateDeepCopy.getTranslation(language).toPromise();
      return this.translate.getParsedResult(translations, key);
  }

  public async getLanguageSubject(): Promise<BehaviorSubject<ILanguage[]>> {
    return this.cachedLanguages$;
  }

  public setTranslation() {
    const locale = this.localizationInfoService.getLocale();
    const cultureInfoNumber = this.localizationInfoService.getCultureInfoNumber();
    const cultureInfoDateTime = this.localizationInfoService.getCultureInfoDatetime();
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    if (locale) {
      this._userLocale$.next(locale);
    } else {
      this._userLocale$.next(this.localizationInfoService.defaultLocale);
    }
    (<QPointIntlService>this.intlService).localeId = this.getKendoUiLanguage(locale);
    (<QPointIntlService>this.intlService).dateFormatLocaleId = cultureInfoDateTime;
    (<QPointIntlService>this.intlService).numberFormatLocaleId = cultureInfoNumber;
  }

  public setMomentLocale(inputMoment: any, locale: string) {
    if (this.checkLocale(inputMoment, locale)) {
      inputMoment.locale(locale);
    } else {
      if (this.LocaleRegex.test(locale)) {
        locale = this.LocaleRegex.exec(locale)[0];
        if (this.checkLocale(inputMoment, locale)) {
          inputMoment.locale(locale);
        } else {
          inputMoment.locale(this.localizationInfoService.defaultLocale);
        }
      } else {
        inputMoment.locale(this.localizationInfoService.defaultLocale);
      }
    }
  }

  private async changeLanguage(language: string) {
    await firstValueFrom(this.translate.use(language));
    this._languageLoaded$.next(language);
    this.setTranslation();
  }

  private checkLocale(list: any, locale: string): boolean {
    try {
      list.localeData(locale);
      return true;
    } catch (ex) {
      return false;
    }
  }

  public setLocales(
    language: string,
    cultureInfoDateTime: string,
    cultureInfoNumber: string,
    translations: Object
  ): void {
    this.translate.setTranslation(language, translations);
    this.translate.use(language);
    this.localeId = cultureInfoDateTime;
    (<QPointIntlService>this.intlService).localeId = this.getKendoUiLanguage(language);
    (<QPointIntlService>this.intlService).dateFormatLocaleId = this.getKendoUiLanguage(cultureInfoDateTime);
    (<QPointIntlService>this.intlService).numberFormatLocaleId = this.getKendoUiLanguage(cultureInfoNumber);
  }

  public getKendoUiLanguage(language: string): string {
    try {
      (<QPointIntlService>this.intlService).numberSymbols(language);
      return language ? language : this.localizationInfoService.defaultLocale;
    } catch (e) {
      return this.localizationInfoService.defaultLocale;
    }
  }

  public formatNumber(value?: number, customFormat?: string | number): string {
    return new QPointNumberFormatPipe(this.intlService).transform(value, customFormat);
  }
}
